import { UseFormReturn } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Zap, CheckCircle2, XCircle, LoaderCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useState } from "react";

/**
 * Masked value placeholder used by the backend to hide sensitive credentials
 */
const MASKED_VALUE = "••••••••";

/**
 * Check if a value is the masked placeholder (credential is stored but hidden)
 */
const isMaskedValue = (value: unknown): boolean => value === MASKED_VALUE;

type ApiConnectionFormProps = {
    form: UseFormReturn<any>;
    placeholders: Record<string, string>;
};

/**
 * Standalone Test Button component that can be placed anywhere
 */
export const ApiTestButton = ({ form }: { form: UseFormReturn<any> }) => {
    const [testResult, setTestResult] = useState<{ success: boolean; message: string; statusCode?: number } | null>(null);
    const [isTesting, setIsTesting] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const testApiConfigMutation = trpc.agentConnections.testApiConfig.useMutation({
        onSuccess: (result) => {
            setTestResult(result);
            setIsTesting(false);
            setIsOpen(true);
        },
        onError: (error) => {
            setTestResult({ success: false, message: error.message || "Connection test failed" });
            setIsTesting(false);
            setIsOpen(true);
        },
    });

    const handleTestConnection = () => {
        const config = form.getValues("config");
        
        if (!config?.apiEndpoint) {
            setTestResult({ success: false, message: "Please enter an API endpoint first" });
            setIsOpen(true);
            return;
        }

        setIsTesting(true);
        setTestResult(null);
        testApiConfigMutation.mutate({
            apiEndpoint: config.apiEndpoint,
            authMethod: config.authMethod || "none",
            bearerToken: config.bearerToken,
            basicUsername: config.basicUsername,
            basicPassword: config.basicPassword,
            apiKeyValue: config.apiKeyValue,
            apiKeyName: config.apiKeyName,
            apiKeyLocation: config.apiKeyLocation,
            oauthToken: config.oauthToken,
            timeoutMs: config.timeoutMs,
        });
    };

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleTestConnection}
                    disabled={isTesting}
                >
                    {isTesting ? (
                        <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                        <Zap className="mr-2 h-4 w-4" />
                    )}
                    {isTesting ? "Testing..." : "Test"}
                </Button>
            </PopoverTrigger>
            {testResult && (
                <PopoverContent className="w-80" align="end">
                    <div className="flex items-start gap-3">
                        {testResult.success ? (
                            <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                        ) : (
                            <XCircle className="h-5 w-5 text-destructive mt-0.5" />
                        )}
                        <div className="flex-1">
                            <div className="flex items-center gap-2 font-medium">
                                {testResult.success ? "Success" : "Failed"}
                                {testResult.statusCode && (
                                    <span className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
                                        {testResult.statusCode}
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1 font-mono break-all">
                                {testResult.message}
                            </p>
                        </div>
                    </div>
                </PopoverContent>
            )}
        </Popover>
    );
};

export const ApiConnectionForm = ({ form, placeholders }: ApiConnectionFormProps) => {
    const authMethod = form.watch("config.authMethod") || "none";

    return (
        <>
            {/* API Endpoint */}
            <FormField
                control={form.control}
                name="config.apiEndpoint"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>API Endpoint</FormLabel>
                        <FormControl>
                            <Input placeholder={placeholders.apiEndpoint || "https://api.example.com/v1"} {...field} />
                        </FormControl>
                        <FormDescription>Base URL of the API you want to connect to</FormDescription>
                        <FormMessage />
                    </FormItem>
                )}
            />

            {/* Default HTTP Method Select */}
            <FormField
                control={form.control}
                name="config.defaultMethod"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Default HTTP Method</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value || "GET"}>
                            <FormControl>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select default HTTP method" />
                                </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                                <SelectItem value="GET">GET</SelectItem>
                                <SelectItem value="POST">POST</SelectItem>
                                <SelectItem value="PUT">PUT</SelectItem>
                                <SelectItem value="PATCH">PATCH</SelectItem>
                                <SelectItem value="DELETE">DELETE</SelectItem>
                            </SelectContent>
                        </Select>
                        <FormDescription>
                            Default HTTP method for API requests (can be overridden per request)
                        </FormDescription>
                        <FormMessage />
                    </FormItem>
                )}
            />

            {/* Auth Method Select */}
            <FormField
                control={form.control}
                name="config.authMethod"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Auth Method</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value || "none"}>
                            <FormControl>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select authentication method" />
                                </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                <SelectItem value="bearer">Bearer Token</SelectItem>
                                <SelectItem value="basic">Basic Auth</SelectItem>
                                <SelectItem value="apiKey">API Key</SelectItem>
                                <SelectItem value="oauth">OAuth 2.0</SelectItem>
                            </SelectContent>
                        </Select>
                        <FormDescription>How to authenticate with the API</FormDescription>
                        <FormMessage />
                    </FormItem>
                )}
            />

            {/* Bearer Token - Show only if authMethod is "bearer" */}
            {authMethod === "bearer" && (
                <FormField
                    control={form.control}
                    name="config.bearerToken"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Bearer Token</FormLabel>
                            <FormControl>
                                <Input
                                    type="password"
                                    placeholder={
                                        isMaskedValue(field.value)
                                            ? "(encrypted - enter new value to update)"
                                            : placeholders.bearerToken || "your-bearer-token-here"
                                    }
                                    value={isMaskedValue(field.value) ? "" : field.value || ""}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    name={field.name}
                                    ref={field.ref}
                                />
                            </FormControl>
                            <FormDescription>The bearer token for Authorization header</FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            )}

            {/* Basic Auth - Show only if authMethod is "basic" */}
            {authMethod === "basic" && (
                <>
                    <FormField
                        control={form.control}
                        name="config.basicUsername"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Basic Username</FormLabel>
                                <FormControl>
                                    <Input placeholder={placeholders.basicUsername || "username"} {...field} />
                                </FormControl>
                                <FormDescription>Username for basic authentication</FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="config.basicPassword"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Basic Password</FormLabel>
                                <FormControl>
                                    <Input
                                        type="password"
                                        placeholder={
                                            isMaskedValue(field.value)
                                                ? "(encrypted - enter new value to update)"
                                                : placeholders.basicPassword || "password"
                                        }
                                        value={isMaskedValue(field.value) ? "" : field.value || ""}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        name={field.name}
                                        ref={field.ref}
                                    />
                                </FormControl>
                                <FormDescription>Password for basic authentication</FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </>
            )}

            {/* API Key - Show only if authMethod is "apiKey" */}
            {authMethod === "apiKey" && (
                <>
                    <FormField
                        control={form.control}
                        name="config.apiKeyValue"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>API Key Value</FormLabel>
                                <FormControl>
                                    <Input
                                        type="password"
                                        placeholder={
                                            isMaskedValue(field.value)
                                                ? "(encrypted - enter new value to update)"
                                                : placeholders.apiKeyValue || "your-api-key"
                                        }
                                        value={isMaskedValue(field.value) ? "" : field.value || ""}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        name={field.name}
                                        ref={field.ref}
                                    />
                                </FormControl>
                                <FormDescription>Your API key</FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="config.apiKeyLocation"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>API Key Location</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value || "header"}>
                                    <FormControl>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select where to place the API key" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        <SelectItem value="header">Header</SelectItem>
                                        <SelectItem value="query">Query Parameter</SelectItem>
                                    </SelectContent>
                                </Select>
                                <FormDescription>Where to include the API key in requests</FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="config.apiKeyName"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>API Key Name</FormLabel>
                                <FormControl>
                                    <Input placeholder={placeholders.apiKeyName || "X-API-Key"} {...field} />
                                </FormControl>
                                <FormDescription>
                                    Name of the header or query parameter (e.g., "X-API-Key", "api_key")
                                </FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </>
            )}

            {/* OAuth - Show only if authMethod is "oauth" */}
            {authMethod === "oauth" && (
                <FormField
                    control={form.control}
                    name="config.oauthToken"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>OAuth Access Token</FormLabel>
                            <FormControl>
                                <Input
                                    type="password"
                                    placeholder={
                                        isMaskedValue(field.value)
                                            ? "(encrypted - enter new value to update)"
                                            : placeholders.oauthToken || "your-oauth-access-token"
                                    }
                                    value={isMaskedValue(field.value) ? "" : field.value || ""}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    name={field.name}
                                    ref={field.ref}
                                />
                            </FormControl>
                            <FormDescription>OAuth 2.0 access token (full OAuth flow coming soon)</FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            )}

            {/* Default Headers - Always visible */}
            <FormField
                control={form.control}
                name="config.defaultHeaders"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Default Headers (Optional)</FormLabel>
                        <FormControl>
                            <Textarea
                                placeholder={placeholders.defaultHeaders || '{"User-Agent": "Junet-API-Agent"}'}
                                className="font-mono text-sm"
                                rows={3}
                                {...field}
                            />
                        </FormControl>
                        <FormDescription>
                            Additional headers as JSON (e.g., {`{"User-Agent": "MyApp", "Accept": "application/json"}`}
                            )
                        </FormDescription>
                        <FormMessage />
                    </FormItem>
                )}
            />

            {/* Timeout - Always visible */}
            <FormField
                control={form.control}
                name="config.timeoutMs"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Timeout (ms)</FormLabel>
                        <FormControl>
                            <Input
                                type="number"
                                placeholder={placeholders.timeoutMs || "30000"}
                                {...field}
                                onChange={(e) => field.onChange(parseInt(e.target.value))}
                            />
                        </FormControl>
                        <FormDescription>Request timeout in milliseconds (default: 30000)</FormDescription>
                        <FormMessage />
                    </FormItem>
                )}
            />
        </>
    );
};
